/**
 * Small floating install-PWA banner. Appears only when:
 *  - browser emitted beforeinstallprompt (deferred prompt is ready), AND
 *  - user hasn't already dismissed this session, AND
 *  - app isn't already installed.
 * User can tap "Install" to trigger the native prompt, or "×" to dismiss.
 */
import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { subscribeInstallPrompt, promptInstall, isInstalled } from "./pwa";

const DISMISS_KEY = "trp_install_dismissed";

export default function PwaInstallPrompt() {
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isInstalled()) return;
    try { if (sessionStorage.getItem(DISMISS_KEY)) return; } catch { /* ignore */ }
    const unsub = subscribeInstallPrompt((ev) => setReady(!!ev));
    return unsub;
  }, []);

  if (!ready) return null;

  async function handleInstall() {
    setBusy(true);
    await promptInstall();
    setBusy(false);
    setReady(false);
  }

  function handleDismiss() {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setReady(false);
  }

  return (
    <div
      data-testid="pwa-install-prompt"
      style={{
        position: "fixed",
        left: 12, right: 12, bottom: 100,
        zIndex: 60,
        padding: "12px 14px",
        background: "rgba(20,20,35,0.96)",
        border: "1px solid rgba(168,85,247,0.35)",
        borderRadius: 14,
        backdropFilter: "blur(20px)",
        display: "flex", alignItems: "center", gap: 12,
        maxWidth: 420, margin: "0 auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
      }}
    >
      <span style={{
        width: 40, height: 40, borderRadius: 10,
        background: "linear-gradient(135deg,#a855f7,#7c3aed)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: "#fff", flexShrink: 0,
      }}>
        <Download size={20} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "#fff", fontSize: 14, fontWeight: 800 }}>
          Install Studio
        </div>
        <div style={{ color: "#a5a5c0", fontSize: 12, lineHeight: 1.3 }}>
          Home-screen app, native feel, faster launch.
        </div>
      </div>
      <button
        type="button"
        onClick={handleInstall}
        disabled={busy}
        data-testid="pwa-install-btn"
        style={{
          padding: "8px 14px", border: 0, borderRadius: 10,
          background: "linear-gradient(135deg,#a855f7,#7c3aed)",
          color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer",
          whiteSpace: "nowrap", opacity: busy ? 0.6 : 1,
        }}
      >
        {busy ? "…" : "Install"}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        data-testid="pwa-install-dismiss"
        aria-label="Dismiss"
        style={{
          width: 28, height: 28, borderRadius: 8,
          background: "transparent", color: "#8b8ba7",
          border: 0, cursor: "pointer",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
